import React from 'react';
import { Link } from 'react-router-dom';
import { FiExternalLink } from 'react-icons/fi';
import { PersonaAvatar } from './PersonaAvatar';
import { PersonaCapabilityMeter } from './PersonaCapabilityMeter';
import { getCapabilityScore } from '../../utils/persona_engine_utils';
import { useTheme } from '../../contexts/ThemeContext';

// v0.4 Task 4: content-only persona card.
//
// INVARIANTS (architect A5):
//   - Props are exactly { persona } — NO isVisible, NO positioning.
//   - Root applies NO: absolute, z-*, translate-y-*, opacity-*.
//   - Caller handles positioning + visibility.
//
// Consumed by:
//   - PersonaHoverCard (shell adds hover positioning + transition)
//   - PeerNodeRich + TurnMarkerNodeRich (inline in ReactFlow nodes)

export interface PersonaCardBodyProps {
  persona: any;
}

export const PersonaCardBody: React.FC<PersonaCardBodyProps> = ({
  persona,
}) => {
  const { darkMode } = useTheme();
  const primarySpecialization =
    persona.primarySpecialization ||
    (persona.tags && persona.tags.length > 0 ? persona.tags[0] : 'general');
  const description =
    persona.shortDescription ||
    persona.fullDescription ||
    persona.prompt ||
    'No description available.';
  const truncatedDescription =
    description.length > 150
      ? description.substring(0, 150) + '...'
      : description;

  return (
    <div
      className={`rounded-lg overflow-hidden ${
        darkMode
          ? 'bg-gray-800 border border-gray-700'
          : 'bg-white border border-gray-200'
      }`}
    >
      <div
        className={`p-4 ${
          darkMode ? 'bg-gray-700' : 'bg-gray-50'
        } border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
      >
        <div className="flex items-center">
          <PersonaAvatar
            name={persona.name}
            specialization={primarySpecialization}
            avatarUrl={persona.avatarUrl}
            size="small"
          />
          <div className="ml-3">
            <h3
              className={`font-bold ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              {persona.name}
            </h3>
            <p
              className={`text-xs ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              {primarySpecialization}
            </p>
          </div>
        </div>
      </div>
      <div className="p-4">
        <p
          className={`text-sm mb-3 ${
            darkMode ? 'text-gray-300' : 'text-gray-600'
          }`}
        >
          {truncatedDescription}
        </p>
        <h4
          className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          Key Capabilities
        </h4>
        <div className="space-y-2 mb-3">
          {Object.entries(persona.capabilities || {})
            .slice(0, 3)
            .map(([key, value]) => (
              <PersonaCapabilityMeter
                key={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={getCapabilityScore(value)}
                max={10}
              />
            ))}
          {Object.keys(persona.capabilities || {}).length === 0 && (
            <p
              className={`text-xs italic ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
            >
              No capabilities defined
            </p>
          )}
        </div>
        <h4
          className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          Tags
        </h4>
        <div className="flex flex-wrap gap-1 mb-3">
          {persona.tags && persona.tags.length > 0 ? (
            persona.tags.slice(0, 5).map((tag: string, index: number) => (
              <span
                key={`tag-${index}`}
                className={`px-2 py-0.5 text-xs rounded-full ${
                  darkMode
                    ? 'bg-blue-900 bg-opacity-50 text-blue-300'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {tag}
              </span>
            ))
          ) : (
            <p
              className={`text-xs italic ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
            >
              No tags defined
            </p>
          )}
        </div>
      </div>
      <div
        className={`p-3 ${
          darkMode ? 'bg-gray-700' : 'bg-gray-50'
        } border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
      >
        <Link
          to={`/personas/${persona.id}`}
          className={`text-xs flex items-center justify-center w-full py-1.5 px-3 rounded ${
            darkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          } transition-colors`}
        >
          <FiExternalLink className="mr-1.5" />
          View Full Profile
        </Link>
      </div>
    </div>
  );
};
